import React, { useEffect, useState } from "react";
import {
  Container,
  Button,
  AppBar,
  Toolbar,
  Paper,
  Typography,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

class PDFImageManager {
  constructor() {
    this.imageCache = new Map();
    this.maxCacheSize = 50;
  }

  isBase64String(str) {
    const base64Patterns = [
      "/9j/",
      "iVBORw0KGgoAAAANSUhEUgAA",
      "R0lGODlh",
      "UklGR",
      "Qk0",
    ];
    return base64Patterns.some((pattern) => str && str.startsWith(pattern));
  }

  formatBase64ForPDF(base64String) {
    if (base64String.startsWith("data:image/")) return base64String;
    let mimeType = "image/jpeg";
    if (base64String.startsWith("iVBORw0KGgoAAAANSUhEUgAA"))
      mimeType = "image/png";
    else if (base64String.startsWith("R0lGODlh")) mimeType = "image/gif";
    else if (base64String.startsWith("UklGR")) mimeType = "image/webp";
    return `data:${mimeType};base64,${base64String}`;
  }

  // Fetch school details from same API as Admission page
async fetchSchoolDetails() {
  try {
    const token = sessionStorage.getItem("token");
    if (!token) throw new Error("No auth token found");

    const response = await fetch('https://wsmsv1.aidigitalsec.com/api/FeeReport/GetSchoolName', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': token },
      body: JSON.stringify({}),
    });
    if (!response.ok) throw new Error(`API error: ${response.status}`);

    const apiData = await response.json();
    const school = apiData[0];

    let logoDataUrl = null;
    const logoImg = school?.logoImg;
    if (logoImg && typeof logoImg === 'string' && logoImg.trim() !== '') {
      try {
        let cleanedBase64 = logoImg
          .replace(/^data:image\/[a-zA-Z]+;base64,/, '')
          .replace(/\s/g, '')
          .replace(/\n/g, '');
        const paddingLength = (4 - (cleanedBase64.length % 4)) % 4;
        cleanedBase64 += '='.repeat(paddingLength);
        if (/^[A-Za-z0-9+/=]+$/.test(cleanedBase64)) {
          logoDataUrl = `data:image/png;base64,${cleanedBase64}`;
        }
      } catch (e) {
        console.warn("Logo processing failed:", e.message);
      }
    }

    return {
      schoolName: school?.schoolName?.replace(/['"]+/g, '') || 'N/A',
      address: school?.address?.replace(/['"]+/g, '') || '',
      phoneNo: school?.phoneNo?.replace(/['"]+/g, '') || '',
      website: school?.website || '',
      logoDataUrl,
    };
  } catch (error) {
    console.error("Failed to fetch school details:", error.message);
    return {
      schoolName: sessionStorage.getItem("organizationName")?.replace(/['"]+/g, '') || 'N/A',
      address: sessionStorage.getItem("address")?.replace(/['"]+/g, '') || '',
      phoneNo: sessionStorage.getItem("phoneNo")?.replace(/['"]+/g, '') || '',
      logoDataUrl: null,
    };
  }
}

// Same robust image-adding logic as Admission.js
async addValidatedImageToPDF(doc, validatedImageResult, x, y, width, height) {
  if (!validatedImageResult || !validatedImageResult.data) {
    this.addPlaceholderToPDF(doc, x, y, width, height);
    return;
  }
  const formats = ['JPEG', 'PNG', 'GIF', 'WEBP'];
  let success = false;
  for (const format of formats) {
    try {
      doc.addImage(validatedImageResult.data, format, x, y, width, height);
      success = true;
      break;
    } catch (e) { /* try next format */ }
  }
  if (!success) this.addPlaceholderToPDF(doc, x, y, width, height);
}

// Card layout — ported from Admission.js, offset by xOffset/yOffset for grid placement
async buildPDFStructure(doc, studentData, schoolDetails, validatedLogoResult, xOffset = 0, yOffset = 0) {
  const pageWidth = 140, pageHeight = 95;
  const navy = [11, 31, 61];
  const orange = [237, 158, 15];
  const contentWidth = 100;

  doc.setFillColor(255, 255, 255);
  doc.rect(xOffset, yOffset, pageWidth, pageHeight, "F");

  if (validatedLogoResult) {
    try {
      await this.addValidatedImageToPDF(doc, validatedLogoResult, xOffset + 5, yOffset + 2, 27, 17);
    } catch (e) { console.warn("Logo add failed:", e.message); }
  }

  let schoolName = schoolDetails.schoolName || "";
  if (!/trust/i.test(schoolName)) schoolName = `${schoolName} (Trust)`;

  doc.setFont("helvetica", "bold");
  let fontSize = 16;
  doc.setFontSize(fontSize);
  const maxTitleWidth = contentWidth + 10;
  while (doc.getTextWidth(schoolName) > maxTitleWidth && fontSize > 8) {
    fontSize -= 0.5;
    doc.setFontSize(fontSize);
  }
  doc.setTextColor(...navy);
  doc.text(schoolName, xOffset + 82, yOffset + 8, { align: "center" });

  doc.setDrawColor(...orange);
  doc.setLineWidth(0.8);
  doc.line(xOffset + 42, yOffset + 10, xOffset + 122, yOffset + 10);

  const bannerText = `STUDENT IDENTITY CARD ${studentData.session?.trim() || "N/A"}`;
  doc.setFontSize(9.5);
  const bannerTextWidth = doc.getTextWidth(bannerText);
  const bannerPadX = 4;
  const bannerCenterX = xOffset + 82;
  const bannerW = bannerTextWidth + bannerPadX * 2;
  const bannerX = bannerCenterX - bannerW / 2;
  const bannerY = yOffset + 11.5;
  const bannerH = 4.5;

  doc.setFillColor(...orange);
  doc.rect(bannerX, bannerY, bannerW, bannerH, "F");
  doc.setTextColor(...navy);
  doc.text(bannerText, bannerCenterX, bannerY + bannerH / 2 + 1.4, { align: "center" });

  doc.setDrawColor(...navy);
  doc.setLineWidth(0.9);
  doc.rect(xOffset + 104, yOffset + 27, 34, 46);
}

// Field layout — ported from Admission.js, offset-aware
addStudentContent(doc, studentData, schoolDetails, xOffset = 0, yOffset = 0) {
  const navy = [11, 31, 61];
  const orange = [237, 158, 15];
  const leftMargin = xOffset + 6;
  const lineEndX = xOffset + 98;

  const drawField = (label, value, x, y, lineEnd) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...navy);
    doc.text(label, x, y);
    const labelWidth = doc.getTextWidth(label);
    const textStartX = x + labelWidth + 2;

    doc.setDrawColor(...orange);
    doc.setLineWidth(0.5);
    doc.line(textStartX, y + 0.8, lineEnd, y + 0.8);

    if (value) {
      doc.setFont("helvetica", "normal");
      doc.text(String(value), textStartX + 1, y);
    }
  };

  let age = "";
  if (studentData.dob) {
    const [d, m, y] = studentData.dob.split("/").map(Number);
    if (d && m && y) {
      const dob = new Date(y, m - 1, d);
      age = Math.floor((new Date() - dob) / (365.25 * 24 * 3600 * 1000));
    }
  }

  let y = yOffset + 26;
  const rowGap = 8.3;

  drawField("NAME:-", studentData.studentName?.trim(), leftMargin, y, lineEndX);
  y += rowGap;
  drawField("PTS CLASS:-", studentData.ptsClass, leftMargin, y, xOffset + 52);
  drawField("SCHOOL CLASS:-", studentData.class, xOffset + 58, y, lineEndX);
  y += rowGap;
  drawField("AGE:-", age, leftMargin, y, xOffset + 28);
  drawField("DATE OF JOINING:-", studentData.dateofAdmission, xOffset + 34, y, lineEndX);
  y += rowGap;
  drawField("PARENT'S NAME:-", studentData.guardianName, leftMargin, y, lineEndX);
  y += rowGap;
  drawField("PARENT'S NUMBER:-", studentData.fatherMobile, leftMargin, y, lineEndX);
  y += rowGap;
  drawField("DATE OF BIRTH:-", studentData.dob, leftMargin, y, lineEndX);
  y += rowGap;
  drawField("ROLL NUMBER:-", studentData.rollNo || "N/A", leftMargin, y, lineEndX);

  const pageWidth = 140, pageHeight = 95, footerH = 10;
  doc.setFillColor(...orange);
  doc.rect(xOffset, yOffset + pageHeight - footerH, pageWidth, footerH, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...navy);
  const footerY = yOffset + pageHeight - footerH / 2 + 1.5;

  doc.text((schoolDetails.website || "PEHCHAANSTREETSCHOOL.ORG").toUpperCase(), xOffset + 6, footerY);
  doc.text(schoolDetails.phoneNo || "", xOffset + pageWidth / 2, footerY, { align: "center" });
  doc.text("AUTH SIGN.", xOffset + pageWidth - 6, footerY, { align: "right" });
}

  async loadImageFromSource(source) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      console.log(`Timeout for ${source.name}`);
      controller.abort();
       }, 15000);
    try {
      console.log(`Fetching image from ${source.name}: ${source.url}`);
            const response = await fetch(source.url, {
        signal: controller.signal,
        mode: "cors",
      });
      clearTimeout(timeoutId);
      console.log(`Response status for ${source.name}: ${response.status}`);
      if (!response.ok)
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      const blob = await response.blob();
      console.log(`Blob type: ${blob.type}, size: ${blob.size}`);
      if (!blob.type.startsWith("image/"))
        throw new Error(`Invalid content type: ${blob.type}`);
      return await this.blobToBase64(blob);
    } catch (error) {
      clearTimeout(timeoutId);
      console.error(`Error loading image from ${source.name}:`, error.message);
      throw error;
    }
  }

  blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = (error) => {
        console.error("FileReader error:", error);
        reject(error);
      };
      reader.readAsDataURL(blob);
    });
  }

  getCachedImage(imagePath) {
    return this.imageCache.get(imagePath);
  }

  cacheImage(imagePath, imageData) {
    if (this.imageCache.size >= this.maxCacheSize) {
      const firstKey = this.imageCache.keys().next().value;
      this.imageCache.delete(firstKey);
    }
    this.imageCache.set(imagePath, imageData);
  }

   getLogoSources(logoUrl) {
    const baseUrl = (process.env.REACT_APP_DOCUMENT_URL || "https://wsmsv1.aidigitalsec.com").replace(/\/$/, "");
    const fullUrl = logoUrl.startsWith("http")
      ? logoUrl
      : `${baseUrl}/${logoUrl.replace(/^[~\/]+/, "")}`;
    return [{ name: "direct", url: fullUrl, method: "direct" }];
  }

 

  async handleLogoImage() {
    console.log("=== LOGO DEBUGGING START ===");
    const logoUrl = sessionStorage
      .getItem("logoImg")
      ?.replace(/['"]+/g, "")
      .trim();
    console.log("Raw logo URL from session:", logoUrl);
    if (!logoUrl || logoUrl === "N/A" || logoUrl === "") {
      console.log("Invalid or missing logo URL, skipping logo");
      return null;
    }
    if (logoUrl.startsWith("data:image/")) {
      console.log("Using direct base64 logo");
      return { type: "base64", data: logoUrl };
    }
    if (this.isBase64String(logoUrl)) {
      console.log("Formatting raw base64 logo");
      return { type: "base64", data: this.formatBase64ForPDF(logoUrl) };
    }
    const cacheKey = `logo_${logoUrl}`;
    const cachedLogo = this.getCachedImage(cacheKey);
    if (cachedLogo) {
      console.log("Using cached logo");
      return { type: "base64", data: cachedLogo };
    }
    const logoSources = this.getLogoSources(logoUrl);
    for (const source of logoSources) {
      try {
        console.log(`Trying logo source: ${source.name}`);
        const logoData = await this.loadImageFromSource(source);
        if (logoData) {
          console.log(`Successfully loaded logo from ${source.name}`);
          this.cacheImage(cacheKey, logoData);
          return { type: "base64", data: logoData };
        }
      } catch (error) {
        console.warn(`Failed to load logo from ${source.name}:`, error.message);
      }
    }
    console.log("All logo sources failed");
    console.log("=== LOGO DEBUGGING END ===");
    return null;
  }

  async handleStudentImage(studentData) {
  const imagePath = [
    studentData.profileImage,
    studentData.studentProfileLogo,
  ].map(v => (v ? String(v).replace(/['"]+/g, "").trim() : ""))
   .find(v => v && v.toUpperCase() !== "N/A");

  console.log(`Processing student image for ${studentData.studentName}: ${imagePath}`);

  if (!imagePath) {
    return this.createDefaultAvatar();
  }

  // Already base64 / data URL
  if (imagePath.startsWith("data:image/")) {
    return { type: "base64", data: imagePath };
  }
  if (this.isBase64String(imagePath)) {
    return { type: "base64", data: this.formatBase64ForPDF(imagePath) };
  }

  const cached = this.getCachedImage(imagePath);
  if (cached) {
    return { type: "base64", data: cached };
  }

  const baseUrl = (process.env.REACT_APP_DOCUMENT_URL || "https://wsmsv1.aidigitalsec.com").replace(/\/$/, "");
  const fullUrl = imagePath.startsWith("http")
    ? imagePath
    : `${baseUrl}/${imagePath.replace(/^[~\/]+/, "")}`;

  try {
    const imageData = await this.loadImageFromSource({ name: "direct", url: fullUrl });
    if (imageData) {
      this.cacheImage(imagePath, imageData);
      return { type: "base64", data: imageData };
    }
  } catch (error) {
    console.warn(`Failed to load student image for ${studentData.studentName}:`, error.message);
  }

  return this.createDefaultAvatar();
}

  createDefaultAvatar() {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    canvas.width = 100;
    canvas.height = 100;
    ctx.fillStyle = "#e0e0e0";
    ctx.beginPath();
    ctx.arc(50, 50, 50, 0, 2 * Math.PI);
    ctx.fill();
    ctx.fillStyle = "#999";
    ctx.beginPath();
    ctx.arc(50, 35, 15, 0, 2 * Math.PI);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(50, 70, 20, 0, Math.PI);
    ctx.fill();
    return { type: "canvas", data: canvas.toDataURL("image/png", 0.8) };
  }

  async addLogoToPDF(doc, logoResult, x, y, width, height) {
    try {
      if (logoResult?.type === "base64") {
        const testImg = new Image();
        testImg.src = logoResult.data;
        await new Promise((resolve, reject) => {
          testImg.onload = () => resolve();
          testImg.onerror = () => reject(new Error("Invalid logo image data"));
        });
        try {
          doc.addImage(logoResult.data, "JPEG", x, y, width, height);
          console.log("Logo added as JPEG");
        } catch (jpegError) {
          console.warn("JPEG failed, trying PNG:", jpegError.message);
          doc.addImage(logoResult.data, "PNG", x, y, width, height);
          console.log("Logo added as PNG");
        }
      }
    } catch (error) {
      console.warn("Failed to add logo:", error.message);
    }
  }

  addImageToPDF(doc, imageResult, x, y, width, height) {
    try {
      if (imageResult.type === "base64" || imageResult.type === "canvas") {
        // Try JPEG first, fallback to PNG if error
        try {
          doc.addImage(imageResult.data, "JPEG", x, y, width, height);
          console.log("Student image added as JPEG");
        } catch (jpegError) {
          console.warn("JPEG failed, trying PNG:", jpegError.message);
          doc.addImage(imageResult.data, "PNG", x, y, width, height);
          console.log("Student image added as PNG");
        }
      } else {
        this.addPlaceholderToPDF(doc, x, y, width, height);
      }
    } catch (error) {
      console.warn("Failed to add student image:", error.message);
      this.addPlaceholderToPDF(doc, x, y, width, height);
    }
  }

  addPlaceholderToPDF(doc, x, y, width, height) {
    doc.setFillColor(240, 240, 240);
    doc.rect(x, y, width, height, "F");
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.2);
    doc.rect(x, y, width, height);
    doc.setFontSize(6);
    doc.setTextColor(150, 150, 150);
    doc.text("Photo", x + width / 2, y + height / 2, { align: "center" });
  }


  
}

const BulkIdCard = () => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const pdfManager = new PDFImageManager();

  const fetchClasses = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      console.log("Fetching classes from:", `${apiUrl}/Enquiry/ddlClassName`);
      console.log("Token:", token);
      const response = await fetch(`${apiUrl}/Enquiry/ddlClassName`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      console.log("Classes response status:", response.status);
      if (!response.ok)
        throw new Error(
          `Failed to fetch classes: ${response.status} ${response.statusText}`
        );
      const data = await response.json();
      console.log("Classes data:", data);

      // Normalize: handle array, wrapped object, or unexpected types
      const classesArray = Array.isArray(data)
        ? data
        : Array.isArray(data?.classes)
        ? data.classes
        : Array.isArray(data?.data)
        ? data.data
        : [];

      setClasses(classesArray);
    } catch (error) {
      console.error("Error fetching classes:", error.message);
      setClasses([]); // Always reset to empty array on error
      setSnackbarMessage(`Failed to fetch classes: ${error.message}`);
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    }
  };

  const fetchSections = async (classId) => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      console.log("Fetching sections for classId:", classId);
      const response = await fetch(
        `${apiUrl}/ClassPromotion/ddlSection_clsId`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({ teacherId: 0, classId }),
        }
      );
      console.log("Sections response status:", response.status);
      if (!response.ok)
        throw new Error(
          `Failed to fetch sections: ${response.status} ${response.statusText}`
        );
      const data = await response.json();
      console.log("Sections data:", data);

      // Normalize: handle array, wrapped object, or unexpected types
      const sectionsArray = Array.isArray(data)
        ? data
        : Array.isArray(data?.sections)
        ? data.sections
        : Array.isArray(data?.data)
        ? data.data
        : [];

      setSections(sectionsArray);
    } catch (error) {
      console.error("Error fetching sections:", error.message);
      setSections([]); // Always reset to empty array on error
      setSnackbarMessage(`Failed to fetch sections: ${error.message}`);
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    }
  };

  const fetchStudents = async () => {
    if (!selectedClass || !selectedSection) return;
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      console.log("Fetching students with:", {
        classId: selectedClass,
        sectionId: selectedSection,
      });
      const response = await fetch(`${apiUrl}/Admission/GetIdCards`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          classId: selectedClass,
          sectionId: selectedSection,
        }),
      });
      console.log("Students response status:", response.status);
      if (!response.ok)
        throw new Error(
          `Failed to fetch students: ${response.status} ${response.statusText}`
        );
      const data = await response.json();
      console.log("Students data:", data);

      // Normalize: handle array, wrapped object, or unexpected types
      const studentsArray = Array.isArray(data)
        ? data
        : Array.isArray(data?.students)
        ? data.students
        : Array.isArray(data?.data)
        ? data.data
        : [];

      if (studentsArray.length === 0) {
        setSnackbarMessage("No students found for selected class and section");
        setOpenSnackbar(true);
        setSnackbarSeverity("error");
      }
      setStudents(studentsArray);
    } catch (error) {
      console.error("Error fetching students:", error.message);
      setStudents([]); // Always reset to empty array on error
      setSnackbarMessage(`Failed to fetch student data: ${error.message}`);
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    if (selectedClass) {
      setSections([]);
      setSelectedSection("");
      fetchSections(selectedClass);
    }
  }, [selectedClass]);

  useEffect(() => {
    if (selectedSection) {
      fetchStudents();
    }
  }, [selectedSection]);

 const generateBulkIdCardPDF = async () => {
  if (!students.length) {
    setSnackbarMessage("No students selected to generate ID cards");
    setOpenSnackbar(true);
    setSnackbarSeverity("error");
    return;
  }

  setLoading(true);
  try {
    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    

    // Fetch school details once — shared across all cards
    const schoolDetails = await pdfManager.fetchSchoolDetails();
    let validatedLogoResult = null;
    if (schoolDetails.logoDataUrl) {
      validatedLogoResult = { type: 'base64', data: schoolDetails.logoDataUrl };
    }

    const cardWidth = 140;
    const cardHeight = 95;
    const marginX = 5;
    const marginY = 5;
    const spacingX = 5;
    const spacingY = 5;
    const cardsPerRow = 2;
    const cardsPerCol = 2;

    const xOffsets = [marginX, marginX + cardWidth + spacingX];
    const yOffsets = [marginY, marginY + cardHeight + spacingY];

    let cardIndex = 0;
    for (let i = 0; i < students.length; i++) {
      const student = students[i];
      const col = cardIndex % cardsPerRow;
      const row = Math.floor(cardIndex / cardsPerRow) % cardsPerCol;
      const xOffset = xOffsets[col];
      const yOffset = yOffsets[row];

      await pdfManager.buildPDFStructure(doc, student, schoolDetails, validatedLogoResult, xOffset, yOffset);

      const imageResult = await pdfManager.handleStudentImage(student);
      await pdfManager.addValidatedImageToPDF(doc, imageResult, xOffset + 106, yOffset + 29, 30, 42);

      pdfManager.addStudentContent(doc, student, schoolDetails, xOffset, yOffset);

      cardIndex++;
      if (cardIndex === cardsPerRow * cardsPerCol && i < students.length - 1) {
        doc.addPage();
        cardIndex = 0;
      }
    }

    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, "_blank");
    setTimeout(() => URL.revokeObjectURL(pdfUrl), 5000);
  } catch (error) {
    console.error("Error generating bulk ID card PDF:", error.message);
    setSnackbarMessage(`Failed to generate bulk ID card PDF: ${error.message}`);
    setOpenSnackbar(true);
    setSnackbarSeverity("error");
  } finally {
    setLoading(false);
  }
};

  return (
    <Container maxWidth="md">
      <AppBar position="static" sx={{ backgroundColor: "#0B1F3D" }}>
        <Toolbar>
          <Typography variant="h4" component="div" style={{ fontWeight: 'bold' }}>
            Bulk ID Card Generation
          </Typography>
        </Toolbar>
      </AppBar>
      <Paper elevation={3} style={{ padding: '20px', marginTop: '6px', textAlign: 'left' }}>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, mt: 2 }}>
          <FormControl fullWidth variant="outlined" sx={{ maxWidth: '100%' }}>
            <InputLabel shrink={!!selectedClass}>Class</InputLabel>
            <Select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              label="Class"
              disabled={loading}
            >
              <MenuItem value="">
                <em>Select Class</em>
              </MenuItem>
              {classes.map((cls) => (
                <MenuItem key={cls.classId} value={cls.classId}>
                  {cls.className}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth variant="outlined" sx={{ maxWidth: '100%' }}>
            <InputLabel shrink={!!selectedSection}>Section</InputLabel>
            <Select
              value={selectedSection}
              onChange={(e) => setSelectedSection(e.target.value)}
              label="Section"
              disabled={loading || !selectedClass}
            >
              <MenuItem value="">
                <em>Select Section</em>
              </MenuItem>
              {sections.map((section) => (
                <MenuItem key={section.sectionId} value={section.sectionId}>
                  {section.sectionName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box display="flex" justifyContent="center" sx={{ mt: 2, gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            sx={{
              color: "#ffffff",
              width: "180px",
              height: "50px",
              padding: "10px 0",
              fontWeight: 'bold',
            }}
            onClick={() => navigate(-1)}
            disabled={loading}
          >
            Back to Admission
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#4caf50",
              color: "#ffffff",
              width: "210px",
              height: "50px",
              padding: "10px 0",
              fontWeight: 'bold',
              "&:hover": { backgroundColor: "#388e3c" },
            }}
            onClick={generateBulkIdCardPDF}
            disabled={loading || !students.length}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? "Generating..." : "Generate Bulk ID Cards"}
          </Button>
        </Box>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default BulkIdCard;